import { isDefined } from "@clipboard-health/util-ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { CssBaseline, Stack, ThemeProvider } from "@mui/material";
import { LoadingButton } from "@src/appV2/lib/LoadingButton/LoadingButton";
import { getTheme } from "@src/appV2/redesign/system/ui-theme";
import { isAxiosError } from "axios";
import { useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useHistory, useLocation } from "react-router-dom";

import { RootPaths } from "../App/paths";
import FetchOtpButtonInDevelopmentMode from "../Debug/components/FetchOtpButtonInDevelopmentMode";
import { extractPhoneNumberDigits, useToast } from "../lib";
import { PageFooterWrapper } from "../redesign/components/PageFooterWrapper";
import { PageWrapper } from "../redesign/components/PageWrapper";
import { StickyScrollPageHeader } from "../redesign/components/StickyScrollPageHeader";
import { useEditAccountReactivationRequest } from "./api/useEditAccountReactivationRequest";
import { enterPhoneNumberFormSchema, verifyOtpFormSchema } from "./Forms/schema";
import { VerifyOtpForm } from "./Forms/VerifyOtpForm";
import { VerifyPhoneForm } from "./Forms/VerifyPhoneForm";
import { ACCOUNT_REACTIVATION_SUCCESS_FULL_PATH } from "./paths";

const enterPhoneNumberFormId = "enter-phone-number-form";
const verifyOtpFormId = "verify-otp-form";

export function ReactivatePhonePage() {
  const history = useHistory();
  const { showSuccessToast } = useToast();

  const { search } = useLocation();

  const queryParams = new URLSearchParams(search);

  const reactivationId = queryParams.get("reactivationId");
  const phoneNumberFromQuery = queryParams.get("phoneNumber");
  const nextPath = queryParams.get("nextPath");

  const verifyOtpFormMethods = useForm({
    defaultValues: {
      otp: "",
    },
    resolver: zodResolver(verifyOtpFormSchema),
  });

  const {
    formState: { isSubmitting: verifyOtpIsSubmitting, isValid: verifyOtpFormIsValid },
    setError: verifyOtpSetError,
    setValue: verifyOtpSetValue,
  } = verifyOtpFormMethods;

  const verifyOtpFormRef = useRef<HTMLFormElement>(null);

  const enterPhoneNumberFormMethods = useForm({
    defaultValues: {
      phoneNumber: phoneNumberFromQuery ?? "",
    },
    resolver: zodResolver(enterPhoneNumberFormSchema),
  });

  const {
    formState: {
      isSubmitting: enterPhoneNumberIsSubmitting,
      isValid: enterPhoneNumberFormIsValid,
      isSubmitSuccessful: enterPhoneFormIsSubmitSuccessful,
    },
    watch: enterPhoneNumberWatch,
    setError: enterPhoneSetError,
  } = enterPhoneNumberFormMethods;

  const phoneNumber = enterPhoneNumberWatch("phoneNumber");
  const phoneNumberProvided =
    isDefined(phoneNumberFromQuery) ||
    (enterPhoneFormIsSubmitSuccessful && phoneNumber.trim() !== "");

  const { mutateAsync: updateAccountReactivationRequest } = useEditAccountReactivationRequest();

  return (
    <ThemeProvider theme={getTheme()}>
      <CssBaseline />
      <PageWrapper variant="tertiary">
        <StickyScrollPageHeader
          title="Verify Phone Number"
          scrollContainerRef={verifyOtpFormRef}
          headerVariant="tertiary"
          onClickBack={() => {
            history.push(RootPaths.HOME);
          }}
        />
        <Stack sx={{ flexDirection: "column", padding: 5, flex: 1, gap: 4, overflow: "auto" }}>
          {!phoneNumberProvided && (
            <FormProvider {...enterPhoneNumberFormMethods}>
              <VerifyPhoneForm
                id={enterPhoneNumberFormId}
                onSubmit={async (formData) => {
                  if (!isDefined(reactivationId)) {
                    return;
                  }

                  try {
                    const phoneNumberDigits = extractPhoneNumberDigits(formData.phoneNumber);

                    await updateAccountReactivationRequest({
                      id: reactivationId,
                      phone: phoneNumberDigits,
                    });
                  } catch (error) {
                    if (isAxiosError(error) && error.response?.status === 422) {
                      enterPhoneSetError("phoneNumber", {
                        message:
                          "We could not verify your phone number. Please try again or contact support for manual verification.",
                      });
                    } else {
                      enterPhoneSetError("phoneNumber", {
                        message: "We could not verify your phone number. Please try again.",
                      });
                    }
                  }
                }}
              />
            </FormProvider>
          )}

          {phoneNumberProvided && (
            <FormProvider {...verifyOtpFormMethods}>
              <VerifyOtpForm
                ref={verifyOtpFormRef}
                id={verifyOtpFormId}
                phoneNumber={phoneNumber}
                onSubmit={async (formData) => {
                  if (!isDefined(reactivationId)) {
                    return;
                  }

                  try {
                    const response = await updateAccountReactivationRequest({
                      id: reactivationId,
                      phoneOtp: formData.otp,
                    });

                    if (response.data.attributes.phoneVerified) {
                      showSuccessToast("Your phone number has been verified.");

                      if (
                        response.data.attributes.phoneVerified &&
                        response.data.attributes.emailVerified
                      ) {
                        // Both factors verified — account reactivation complete.
                        // Cognito session will be established by the auth listener
                        // reacting to the backend state.
                        history.push(ACCOUNT_REACTIVATION_SUCCESS_FULL_PATH);
                        return;
                      }

                      if (isDefined(nextPath) && !response.data.attributes.emailVerified) {
                        history.push(nextPath);
                        return;
                      }
                    }

                    verifyOtpSetError("otp", {
                      message: "We could not verify your phone number. Please try again.",
                    });
                  } catch (error) {
                    if (isAxiosError(error) && error.response?.status === 401) {
                      verifyOtpSetError("otp", {
                        message:
                          "The verification code you entered was incorrect. Please try again.",
                      });
                    } else {
                      verifyOtpSetError("otp", {
                        message: "We could not verify your phone number. Please try again.",
                      });
                    }
                  }
                }}
              />
              <FetchOtpButtonInDevelopmentMode
                phoneNumber={phoneNumber}
                onSuccess={(otp) => {
                  verifyOtpSetValue("otp", otp);
                  verifyOtpFormRef.current?.dispatchEvent(
                    new Event("submit", { cancelable: true, bubbles: true })
                  );
                }}
              />
            </FormProvider>
          )}
        </Stack>

        <PageFooterWrapper>
          {phoneNumberProvided ? (
            <LoadingButton
              fullWidth
              type="submit"
              variant="contained"
              isLoading={verifyOtpIsSubmitting}
              disabled={!verifyOtpFormIsValid}
              form={verifyOtpFormId}
            >
              Verify
            </LoadingButton>
          ) : (
            <LoadingButton
              fullWidth
              type="submit"
              variant="contained"
              isLoading={enterPhoneNumberIsSubmitting}
              disabled={!enterPhoneNumberFormIsValid}
              form={enterPhoneNumberFormId}
            >
              Send OTP Code
            </LoadingButton>
          )}
        </PageFooterWrapper>
      </PageWrapper>
    </ThemeProvider>
  );
}
